import { useContext, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import CheckIcon from '@mui/icons-material/Check';
import { useLocation, useNavigate } from 'react-router-dom';
import { getDatesForWeek, getHoursByDate, getIsPerDiemByDate } from '../../helpers';
import { getHoursAndMinutes, getNumberWithDelimeter } from '../../../../../utils/helpers';
import AddGradientIcon from '../../../../common/Icons/AddGradientIcon';
import EditTimesheetModal from '../EditTimesheetModal/EditTimesheetModal';
import MainButton from '../../../../common/buttons/MainButton';
import TimesheetItem from '../EditTimesheetModal/components/TimesheetItem';
import BagageIcon from '../../../../common/Icons/BagageIcon';
import NotificationModal from '../../../../common/ModalWindows/NotificationModal';
import { EditPerDiemModal } from '../../../../common/ModalWindows/TNEModals/index';
import BaseSwipeableDrawer from '../../../../common/BaseSwipeableDrawer/BaseSwipeableDrawer';
import classes from './TimesheetsCardList.module.scss';
import colors from '../../../../../scss/colors';
import { months, responsiveSizeTimesheet, timesheetsStatuses } from '../../../../../utils/constants';
import EditMonthlyTimesheetDrawer from '../EditMonthlyTimesheetDrawer/EditMonthlyTimesheetDrawer';
import MonthCalendar from '../../../../common/MonthCalendar/MonthCalendar';
import { generalEnums, rateEnum, submissionEnum } from '../../../../../utils/enums';
import { EditContext } from '../../../../../context/editContexts';
import NavigationComponent from '../../../../common/NavigationComponent/NavigationComponent';
import MobileHeadMenu from '../../components/MobileHeadMenu/MobileHeadMenu';
import { CloseIcon } from '../../../../common/Icons';
import TimesheetsHeader from '../../TimesheetsHeader/TimesheetsHeader';
import { useWindowSize } from '../../../../../utils/hooks';
import KeyValueLabel from '../../../../common/labels/KeyValueLabel/KeyValueLabel';
import StatusLabel from '../../../../common/labels/StatusLabel/StatusLabel';
import Attachments from '../../Attachments/Attachments';
import { setNotification } from '../../../../../actions/appNotifications';
import { changePerDiem, updateTimesheet } from '../../../../../actions/timesheet';

const PerDiemCard = ({ days = 0, clickHandler = () => {} }) => {
  const { t } = useTranslation();

  return (
    <div role="presentation" className={classes.card} onClick={clickHandler}>
      <div className={classes.doubleText}>
        <p className={classes.perDiemLabel}>{t('dashboard.timesheet.perdiem')}</p>
      </div>
      <div className={classes.hoursWrapper}>
        <p className={classes.perDiemDays}>
          {days} {t('common.general.day')}
          {days !== 1 ? 's' : ''}
        </p>
      </div>
    </div>
  );
};

const PerDiemInfoItem = ({ day, value }) => {
  return (
    <div className={classes.infoContainer}>
      <p className={classes.dayInfo}>{day}</p>
      {value ? (
        <CheckIcon sx={{ fontSize: '16px', color: colors.PRIMARY_BLUE }} />
      ) : (
        <CheckIcon sx={{ fontSize: '16px', color: colors.WHITE }} />
      )}
    </div>
  );
};

const DummySelectionComponent = ({ onClickHandler }) => {
  return (
    <div
      className="dummy p-5 border-2 border-gray-light rounded-2xl min-w-[300px] flex"
      role="presentation"
      onClick={onClickHandler}
    >
      <div className="flex justify-between w-full">
        <span className="text-nowrap text-navy-blue">Select an Approver</span>
        <ArrowForwardIosIcon sx={{ opacity: 0.5 }} />
      </div>
    </div>
  );
};

export default function TimesheetsCardList(props) {
  const {
    timesheet,
    data,
    perdiems,
    massiveChangePerDiem,
    startDateOfPeriod,
    addHandler,
    mobileChangeHandler,
    allowedWorkTypes,
    rateType = rateEnum.HOURLY,
    deleteLineHandler,
    submissionType,
    currentMonth,
    editable,
    status,
    author,
    placementStatus,
    placementHandler,
    approvers,
    created,
    deleteHandler,
    copyHandler,
    sectionHandler,
    setOpenPop,
    exportHandler
  } = props;
  const { t } = useTranslation();
  const ctx = useContext(EditContext);
  const isApprover = useSelector((state) => state.accessToParts.tAndEApprover);
  const timesheetPlacementStatus = useSelector((state) => state?.timesheet?.validate?.timesheet.IsValidPlacementStatus);
  const approverList = useSelector((state) => state.user.approvers);
  const [openTimesheet, setOpenTimesheet] = useState(null);
  const [openSectionId, setOpenSectionId] = useState(null);
  const [openPerDiems, setOpenPerDiems] = useState(null);
  const [approver, setApprover] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const navigate = useNavigate();
  const locationQueryParams = useLocation();
  const dispatch = useDispatch();
  const size = useWindowSize();

  const openSection = useMemo(() => {
    if (!openSectionId || !timesheet?.TimesheetSections) return null;
    return timesheet.TimesheetSections.find((sec) => sec.Id === openSectionId) || null;
  }, [openSectionId, timesheet?.TimesheetSections]);

  const addHandlerMobile = () => {
    const dispatchAdd = addHandler(openSection?.Id);

    if (typeof dispatchAdd === 'function') {
      dispatchAdd();
    }

    setIsCreating(true);
  };

  const isSplit = timesheet?.IsSplitApprovalFlow;
  const perdiemSource = (section) => (isSplit ? section?.TimesheetPerDiem : perdiems);

  useEffect(() => {
    if (isCreating) {
      if (timesheet?.IsSplitApprovalFlow) {
        const updatedDetails = openSection?.TimesheetDetails || [];

        if (updatedDetails && updatedDetails.length > 0) {
          setOpenTimesheet({
            timesheet: updatedDetails[updatedDetails.length - 1],
            startDateOfPeriod
          });
          setIsCreating(false);
        } else if (data && data.length > 0) {
          setOpenTimesheet({
            timesheet: data[data.length - 1],
            startDateOfPeriod
          });
          setIsCreating(false);
        }
      }
    }
  }, [data, openSection, isCreating, timesheet?.IsSplitApprovalFlow, startDateOfPeriod, setOpenTimesheet]);

  const savePDHandler = (values) => {
    if (!timesheet?.IsSplitApprovalFlow) {
      dispatch(
        changePerDiem({
          IsSplitApprovalFlow: false,
          SectionId: null,
          Data: values.map((v) => ({ ...v, Date: dayjs(v.Date).format('YYYY-MM-DDTHH:mm:ss') })),
          isMobile: true
        })
      );
    } else {
      dispatch(
        changePerDiem({
          IsSplitApprovalFlow: true,
          SectionId: openSection?.Id,
          Data: values,
          isMobile: true
        })
      );
    }
    ctx.setHasChanges(true);
    setOpenPerDiems(null);
  };

  const closePDHandler = () => {
    setOpenPerDiems(null);
  };
  const deletePDHandler = (values) => {
    const clearedValues = values.map((value) => ({ ...value, IsPerDiem: false }));
    massiveChangePerDiem(clearedValues);
    dispatch(
      changePerDiem({
        IsSplitApprovalFlow: timesheet?.IsSplitApprovalFlow,
        SectionId: openSection?.Id ?? null,
        Data: clearedValues,
        isMobile: true
      })
    );
    setOpenPerDiems(null);
  };
  const getTotalHours = (timesheetItem) => {
    if (
      submissionType === submissionEnum.MONTHLY ||
      submissionType === submissionEnum.BI_MONTHLY ||
      submissionType === submissionEnum.BI_WEEKLY
    )
      return timesheetItem.TimesheetHoursByDates.reduce((acc, ts) => acc + ts.TotalHrs, 0)
        .toFixed(2)
        .toString();
    const totalHoursArray = [
      getHoursByDate(timesheetItem.TimesheetHoursByDates, dayjs(startDateOfPeriod)),
      getHoursByDate(timesheetItem.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(1, 'day')),
      getHoursByDate(timesheetItem.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(2, 'day')),
      getHoursByDate(timesheetItem.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(3, 'day')),
      getHoursByDate(timesheetItem.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(4, 'day')),
      getHoursByDate(timesheetItem.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(5, 'day')),
      getHoursByDate(timesheetItem.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(6, 'day'))
    ];
    return parseFloat(totalHoursArray.reduce((accumulator, hours) => accumulator + Number(hours), 0))
      .toFixed(2)
      .toString();
  };
  const getTotalPerDiems = (passedPerdiems) => {
    if (passedPerdiems) {
      if (
        submissionType === submissionEnum.MONTHLY ||
        submissionType === submissionEnum.BI_MONTHLY ||
        submissionType === submissionEnum.BI_WEEKLY
      )
        return passedPerdiems.reduce((acc, pd) => (pd.IsPerDiem ? acc + 1 : acc), 0);
      const totalPerDiemsArray = [
        getIsPerDiemByDate(passedPerdiems, dayjs(startDateOfPeriod)),
        getIsPerDiemByDate(passedPerdiems, dayjs(startDateOfPeriod).add(1, 'day')),
        getIsPerDiemByDate(passedPerdiems, dayjs(startDateOfPeriod).add(2, 'day')),
        getIsPerDiemByDate(passedPerdiems, dayjs(startDateOfPeriod).add(3, 'day')),
        getIsPerDiemByDate(passedPerdiems, dayjs(startDateOfPeriod).add(4, 'day')),
        getIsPerDiemByDate(passedPerdiems, dayjs(startDateOfPeriod).add(5, 'day')),
        getIsPerDiemByDate(passedPerdiems, dayjs(startDateOfPeriod).add(6, 'day'))
      ];
      return totalPerDiemsArray.reduce((accumulator, perDiem) => (perDiem ? accumulator + 1 : accumulator), 0);
    }
    return undefined;
  };
  const getListOfPerDiem = (section) => {
    const perDiemSource = perdiemSource(section);
    if (
      submissionType === submissionEnum.MONTHLY ||
      submissionType === submissionEnum.BI_MONTHLY ||
      submissionType === submissionEnum.BI_WEEKLY
    )
      return perDiemSource.map((pd) => ({ IsPerDiem: pd.IsPerDiem, Date: dayjs(pd.Date) }));
    return [
      { Date: dayjs(startDateOfPeriod), IsPerDiem: getIsPerDiemByDate(perDiemSource, dayjs(startDateOfPeriod)) },
      {
        Date: dayjs(startDateOfPeriod).add(1, 'day'),
        IsPerDiem: getIsPerDiemByDate(perDiemSource, dayjs(startDateOfPeriod).add(1, 'day'))
      },
      {
        Date: dayjs(startDateOfPeriod).add(2, 'day'),
        IsPerDiem: getIsPerDiemByDate(perDiemSource, dayjs(startDateOfPeriod).add(2, 'day'))
      },
      {
        Date: dayjs(startDateOfPeriod).add(3, 'day'),
        IsPerDiem: getIsPerDiemByDate(perDiemSource, dayjs(startDateOfPeriod).add(3, 'day'))
      },
      {
        Date: dayjs(startDateOfPeriod).add(4, 'day'),
        IsPerDiem: getIsPerDiemByDate(perDiemSource, dayjs(startDateOfPeriod).add(4, 'day'))
      },
      {
        Date: dayjs(startDateOfPeriod).add(5, 'day'),
        IsPerDiem: getIsPerDiemByDate(perDiemSource, dayjs(startDateOfPeriod).add(5, 'day'))
      },
      {
        Date: dayjs(startDateOfPeriod).add(6, 'day'),
        IsPerDiem: getIsPerDiemByDate(perDiemSource, dayjs(startDateOfPeriod).add(6, 'day'))
      }
    ];
  };

  const timesheetPeriodLabel = useMemo(() => {
    const details = timesheet?.TimesheetDetails?.[0];
    const days = details?.TimesheetHoursByDates;
    if (!days || !days.length || !submissionType) return '';
    const startDay = dayjs(days[0].Day);
    const endDay = dayjs(days.at(-1).Day);
    const isBiWeekly = submissionType === submissionEnum.BI_WEEKLY || submissionType === submissionEnum.BI_MONTHLY;
    const isMonthly = submissionType === submissionEnum.MONTHLY;
    if (isMonthly) return months(t)[startDay.get('month')];
    if (isBiWeekly) {
      return startDay.isSame(endDay, 'month')
        ? `${startDay.format('MMMM')} (${startDay.format('D')}-${endDay.format('D')})`
        : `${startDay.format('D MMM')} - ${endDay.format('D MMM')}`;
    }
    return `${startDay.format('D MMM')} - ${endDay.format('D MMM')}`;
  }, [timesheet?.TimesheetDetails, submissionType, t]);

  const handleApproverSelection = (newValue, sectionId, section) => {
    const hasDuplicate = timesheet?.TimesheetSections?.some(
      (s) => s.Id !== sectionId && s.ApproverId === newValue?.value
    );

    if (hasDuplicate) {
      dispatch(
        setNotification({
          type: 'error',
          message: 'This approver is already selected in another section.',
          duration: 3000
        })
      );

      const updatedSection = {
        ...section,
        Approver: '',
        ApproverId: null
      };

      dispatch(updateTimesheet(updatedSection, sectionId, timesheet?.IsSplitApprovalFlow));
      return;
    }

    const updatedSection = {
      ...section,
      Approver: newValue,
      ApproverId: newValue?.value || null
    };

    dispatch(updateTimesheet(updatedSection, sectionId, timesheet?.IsSplitApprovalFlow));
    ctx.setHasChanges(true);
  };

  return (
    <div className={classes.list}>
      <NavigationComponent
        label={
          openSection !== null
            ? approverList?.find((appr) => appr.value === openSection.ApproverId)?.label
            : timesheetPeriodLabel
        }
        linkBack={!created && !ctx?.hasChanges ? `/dashboard/timesheets${locationQueryParams.search}` : 'x'}
        onClick={
          openSection !== null
            ? () => setOpenSectionId(null)
            : () => navigate(`/dashboard/timesheets${locationQueryParams.search}`)
        }
      >
        {!created && !ctx?.hasChanges ? (
          <MobileHeadMenu
            isApprover={isApprover}
            isSectionOpen={openSection !== null}
            data={timesheet}
            deleteHandler={() => (openSection !== null ? deleteHandler(openSection?.Id) : deleteHandler())}
            copyHandler={copyHandler}
            pageType={generalEnums.TIMESHEET}
            timesheetPlacementStatus={timesheetPlacementStatus}
            isInfoIcon={[timesheetsStatuses(t).draft.apiStatus].includes(timesheet.Status.Status)}
            exportHandler={exportHandler}
          />
        ) : (
          <CloseIcon sx={{ cursor: 'pointer' }} onClick={() => navigate('/dashboard/timesheets')} />
        )}
      </NavigationComponent>
      <div className="w-full flex justify-between align-middle">
        <TimesheetsHeader
          timesheet={timesheet}
          submissionType={submissionType}
          isApprover={isApprover}
          created={created}
        />
        {((![timesheetsStatuses(t).processed.apiStatus].includes(timesheet?.Status?.Status) &&
          (!isApprover || timesheetsStatuses(t).rejected.apiStatus !== timesheet?.Status?.Status) &&
          (isApprover || timesheetsStatuses(t).approved.apiStatus !== timesheet?.Status?.Status)) ||
          size.width <= responsiveSizeTimesheet) && (
          <KeyValueLabel
            label=""
            value={
              <StatusLabel
                type={timesheet?.Status?.Status.toLowerCase()}
                text={timesheet?.Status?.Status}
                isApprover={isApprover}
              />
            }
          />
        )}
      </div>

      {!!timesheet?.IsSplitApprovalFlow && openSection !== null && (
        <div className="w-full flex flex-col gap-4">
          {openSection?.TimesheetDetails?.map((ts) => (
            <div
              role="presentation"
              key={ts.Id}
              className={classes.card}
              onClick={() => setOpenTimesheet({ timesheet: ts, startDateOfPeriod })}
            >
              <div className={classes.doubleText}>
                <p className={classes.location}>{ts?.Location}</p>
                <p className={classes.worktype}>{ts?.WorkType.Name}</p>
              </div>
              <div className={classes.hoursWrapper}>
                <p className={classes.hours}>
                  {(submissionType === submissionEnum.MONTHLY ||
                    submissionType === submissionEnum.BI_MONTHLY ||
                    submissionType === submissionEnum.BI_WEEKLY) &&
                    rateType === rateEnum.HOURLY &&
                    getHoursAndMinutes(ts.TimesheetHoursByDates.reduce((acc, item) => acc + item.TotalHrs, 0))}
                  {(submissionType === submissionEnum.MONTHLY ||
                    submissionType === submissionEnum.BI_MONTHLY ||
                    submissionType === submissionEnum.BI_WEEKLY) &&
                    rateType === rateEnum.DAILY &&
                    `${parseFloat(
                      ts.TimesheetHoursByDates.reduce((acc, item) => acc + item.TotalHrs, 0)
                        .toFixed(2)
                        .toString()
                    )} d`}
                  {submissionType === submissionEnum.WEEKLY &&
                    rateType === rateEnum.HOURLY &&
                    getHoursAndMinutes(getTotalHours(ts, startDateOfPeriod))}
                  {submissionType === submissionEnum.WEEKLY &&
                    rateType === rateEnum.DAILY &&
                    `${parseFloat(getTotalHours(ts, startDateOfPeriod))} d`}
                </p>
              </div>
              <div className={classes.arrow}>
                <ArrowForwardIosIcon sx={{ opacity: 0.1 }} />
              </div>
            </div>
          ))}
          {!!getTotalPerDiems(perdiemSource(openSection)) && (
            <PerDiemCard
              days={getTotalPerDiems(perdiemSource(openSection))}
              clickHandler={() =>
                setOpenPerDiems({
                  data: getListOfPerDiem(openSection),
                  isUpdate: true,
                  submissionType,
                  currentMonth,
                  savePDHandler,
                  closePDHandler,
                  deletePDHandler
                })
              }
            />
          )}
          <div className="w-full flex gap-2 py-5">
            {!isApprover &&
              ![timesheetsStatuses(t).approved.apiStatus, timesheetsStatuses(t).processed.apiStatus].includes(
                status
              ) && (
                <div role="presentation" onClick={addHandlerMobile}>
                  <AddGradientIcon />
                </div>
              )}
            {!getTotalPerDiems(timesheet?.IsSplitApprovalFlow ? openSection?.TimesheetPerDiem : perdiems) &&
              !isApprover && (
                <div
                  role="presentation"
                  onClick={() =>
                    setOpenPerDiems({
                      data: getListOfPerDiem(openSection),
                      isUpdate: false,
                      submissionType,
                      currentMonth,
                      savePDHandler,
                      closePDHandler,
                      deletePDHandler
                    })
                  }
                >
                  <BagageIcon />
                </div>
              )}
          </div>
          <Attachments
            status={openSection?.Status?.Status}
            editeMode={ctx.isEdit}
            timesheetId={openSection?.Id}
            attachments={openSection?.TimesheetAttachments}
            handleChanges={ctx.setHasChanges}
          />
        </div>
      )}
      {!!timesheet?.IsSplitApprovalFlow && openSection === null && (
        <>
          {timesheet?.TimesheetSections.map((section) => (
            <div
              role="presentation"
              key={section.Id}
              className={classes.card}
              style={{
                borderLeft: `3px solid ${
                  timesheetsStatuses(t)[section?.Status?.Status?.toLowerCase()]?.color ||
                  timesheetsStatuses(t)?.draft?.color
                }`
              }}
              onClick={() => (section?.ApproverId ? setOpenSectionId(section.Id) : () => {})}
            >
              {section?.ApproverId ? (
                <>
                  <div className={classes.doubleText}>
                    <p className={classes.location}>
                      {approverList?.find((appr) => appr.value === section.ApproverId)?.label}
                    </p>
                    <p
                      style={{
                        backgroundColor: timesheetsStatuses(t)[section?.Status?.Status?.toLowerCase()]?.color
                      }}
                      className="text-white px-2 py-0.5 rounded-lg text-xs"
                    >
                      {section?.Status?.SectionUIStatus}
                    </p>
                  </div>
                  <div className={classes.hoursWrapper}>
                    <p className={classes.hours}>{/* Section hours logic if applicable */}</p>
                  </div>
                  <div className={classes.arrow}>
                    <ArrowForwardIosIcon sx={{ opacity: 0.1 }} />
                  </div>
                </>
              ) : (
                <DummySelectionComponent
                  onClickHandler={() => {
                    setOpenTimesheet({
                      timesheet: section.TimesheetDetails[0],
                      startDateOfPeriod
                    });
                    setOpenSectionId(section.Id);
                  }}
                />
              )}
            </div>
          ))}
          {!isApprover &&
            ![timesheetsStatuses(t).approved.apiStatus, timesheetsStatuses(t).processed.apiStatus].includes(status) && (
              <div className="w-full" role="presentation" onClick={sectionHandler}>
                <AddGradientIcon />
                <span className="text-sm pl-3 text-navy-blue">New Approver Section</span>
              </div>
            )}
        </>
      )}
      {!timesheet?.IsSplitApprovalFlow && (
        <>
          {data.map((ts) => (
            <div
              role="presentation"
              key={ts.Id}
              className={classes.card}
              onClick={() => setOpenTimesheet({ timesheet: ts, startDateOfPeriod })}
            >
              <div className={classes.doubleText}>
                <p className={classes.location}>{ts?.Location}</p>
                <p className={classes.worktype}>{ts?.WorkType.Name}</p>
              </div>
              <div className={classes.hoursWrapper}>
                <p className={classes.hours}>
                  {(submissionType === submissionEnum.MONTHLY ||
                    submissionType === submissionEnum.BI_MONTHLY ||
                    submissionType === submissionEnum.BI_WEEKLY) &&
                    rateType === rateEnum.HOURLY &&
                    getHoursAndMinutes(ts.TimesheetHoursByDates.reduce((acc, item) => acc + item.TotalHrs, 0))}
                  {(submissionType === submissionEnum.MONTHLY ||
                    submissionType === submissionEnum.BI_MONTHLY ||
                    submissionType === submissionEnum.BI_WEEKLY) &&
                    rateType === rateEnum.DAILY &&
                    `${parseFloat(
                      ts?.TimesheetHoursByDates?.reduce((acc, item) => acc + item.TotalHrs, 0)
                        .toFixed(2)
                        .toString()
                    )} d`}
                  {submissionType === submissionEnum.WEEKLY &&
                    rateType === rateEnum.HOURLY &&
                    getHoursAndMinutes(getTotalHours(ts, startDateOfPeriod))}
                  {submissionType === submissionEnum.WEEKLY &&
                    rateType === rateEnum.DAILY &&
                    `${parseFloat(getTotalHours(ts, startDateOfPeriod))} d`}
                </p>
              </div>
              <div className={classes.arrow}>
                <ArrowForwardIosIcon sx={{ opacity: 0.1 }} />
              </div>
            </div>
          ))}
          {!!getTotalPerDiems(perdiems) && (
            <PerDiemCard
              days={getTotalPerDiems(perdiems)}
              clickHandler={() =>
                setOpenPerDiems({
                  data: getListOfPerDiem(),
                  isUpdate: true,
                  submissionType,
                  currentMonth,
                  savePDHandler,
                  closePDHandler,
                  deletePDHandler
                })
              }
            />
          )}
          <div className={classes.btnsContainer}>
            {!isApprover &&
              ![timesheetsStatuses(t).approved.apiStatus, timesheetsStatuses(t).processed.apiStatus].includes(
                status
              ) && (
                <div role="presentation" onClick={addHandlerMobile}>
                  <AddGradientIcon />
                </div>
              )}
            {!getTotalPerDiems(perdiems) && !isApprover && (
              <div
                role="presentation"
                onClick={() =>
                  setOpenPerDiems({
                    data: getListOfPerDiem(),
                    isUpdate: false,
                    submissionType,
                    currentMonth,
                    savePDHandler,
                    closePDHandler,
                    deletePDHandler
                  })
                }
              >
                <BagageIcon />
              </div>
            )}
          </div>
        </>
      )}
      {/* --- Modals and Drawers --- */}
      {openPerDiems && editable && (
        <NotificationModal
          open={openPerDiems}
          onClose={closePDHandler}
          paddingX="5px"
          backgroundColor={colors.PAGE_BACKGROUND}
        >
          <EditPerDiemModal dataSet={openPerDiems} />
        </NotificationModal>
      )}
      {openTimesheet && editable && submissionType === submissionEnum.WEEKLY && (
        <EditTimesheetModal
          open={openTimesheet}
          data={openTimesheet}
          allowedWorkTypes={allowedWorkTypes}
          startDateOfPeriod={startDateOfPeriod}
          saveHandler={(newData) => {
            mobileChangeHandler(newData, openSection?.Id);
            setOpenTimesheet(null);
          }}
          closeHandler={() => setOpenTimesheet(null)}
          submissionType={submissionType}
          rateType={rateType}
          deleteLineHandler={deleteLineHandler}
          currentMonth={currentMonth}
          editable={editable}
          placementStatus={placementStatus}
          placementHandler={placementHandler}
        />
      )}
      {openTimesheet &&
        editable &&
        (submissionType === submissionEnum.MONTHLY ||
          submissionType === submissionEnum.BI_MONTHLY ||
          submissionType === submissionEnum.BI_WEEKLY) && (
          <EditMonthlyTimesheetDrawer
            approverSelectionProps={{
              openSection,
              approver,
              setOpenPop,
              setApprover,
              changeHandler: handleApproverSelection,
              approvers
            }}
            data={openTimesheet}
            closeHandler={() => setOpenTimesheet(null)}
            allowedWorkTypes={allowedWorkTypes}
            saveHandler={(newData) => {
              mobileChangeHandler(newData, openSection?.Id);
              setOpenTimesheet(null);
            }}
            rateType={rateType}
            deleteLineHandler={deleteLineHandler}
            currentMonth={currentMonth}
            placementStatus={placementStatus}
            placementHandler={placementHandler}
            timesheetType={submissionType}
            isSplitApprovalFlow={timesheet?.IsSplitApprovalFlow}
          />
        )}
      {openPerDiems && !editable && (
        <BaseSwipeableDrawer height="auto" closeHandler={() => setOpenPerDiems(null)}>
          <div className={classes.swiper} />
          <div className={classes.container}>
            <div className={classes.header}>
              <p>{t('dashboard.timesheet.perdiem')}</p>
            </div>
            <div className={classes.timesheet}>
              {submissionType === submissionEnum.WEEKLY ? (
                getDatesForWeek(startDateOfPeriod).map((date) => (
                  <PerDiemInfoItem
                    key={date}
                    day={dayjs(date).format('ddd DD')}
                    value={getIsPerDiemByDate(perdiems, date)}
                  />
                ))
              ) : (
                <MonthCalendar
                  data={perdiems}
                  dateProperty="Date"
                  valueProperty="IsPerDiem"
                  monthIndex={currentMonth}
                  disabled
                  timesheetType={submissionType}
                />
              )}
            </div>
            <div className={`${classes.total} ${classes.withoutBorder}`}>
              <div className={classes.wrapper}>
                <p>{t('dashboard.timesheet.table.total')}</p>
                <p>
                  {getTotalPerDiems(perdiems)} {t('common.general.day')}
                  {getTotalPerDiems(perdiems) !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
          <MainButton size="small" variant="primary" rounded forceWidth="90%" action={() => setOpenPerDiems(null)}>
            {t('common.general.close')}
          </MainButton>
        </BaseSwipeableDrawer>
      )}
      {openTimesheet && !editable && (
        <BaseSwipeableDrawer zIndex={1599} closeHandler={() => setOpenTimesheet(null)}>
          <div className={classes.swiper} />
          <div className={classes.container}>
            <div className={classes.header}>
              <p>{openTimesheet.timesheet.Location}</p>
              <p>{allowedWorkTypes.find((type) => type.Id === openTimesheet.timesheet.WorkType.Id).Name}</p>
            </div>
            <div className={classes.timesheet}>
              {submissionType === submissionEnum.WEEKLY ? (
                getDatesForWeek(startDateOfPeriod).map((date) => (
                  <TimesheetItem
                    key={date}
                    justForInfo
                    day={dayjs(date).format('ddd DD')}
                    value={getNumberWithDelimeter(getHoursByDate(openTimesheet.timesheet.TimesheetHoursByDates, date))}
                  />
                ))
              ) : (
                <MonthCalendar
                  data={openTimesheet.timesheet.TimesheetHoursByDates}
                  dateProperty="Day"
                  valueProperty="TotalHrs"
                  monthIndex={currentMonth}
                  disabled
                  timesheetType={submissionType}
                />
              )}
            </div>
            <div className={classes.total}>
              <div className={classes.wrapper}>
                <p>{`${t('dashboard.timesheet.table.total')} ${
                  submissionType === submissionEnum.WEEKLY ? t('common.general.week') : ''
                }`}</p>
                <p>{getTotalHours(openTimesheet.timesheet)}</p>
              </div>
            </div>
            {openTimesheet.timesheet.CostCode && (
              <div className={classes.total}>
                <div className={`${classes.wrapper} flex !flex-col !items-baseline`}>
                  <p>{`${t('dashboard.timesheet.table.code')}`}</p>
                  <p className="break-all">{openTimesheet.timesheet.CostCode}</p>
                </div>
              </div>
            )}
            <div className={classes.comment}>
              <div className={classes.contractorName}>
                {!isApprover ? (
                  <p>{t('dashboard.timesheet.table.comment')}</p>
                ) : (
                  <>
                    <p>{t('dashboard.timesheet.table.messagefrom')}</p>&nbsp;
                    <p>{`${author.FirstName} ${author.LastName}`}</p>
                  </>
                )}
              </div>
              <div className={classes.message}>{openTimesheet.timesheet.Comment}</div>
            </div>
          </div>
          <div className={classes.fixed}>
            <MainButton size="small" variant="primary" rounded forceWidth="90%" action={() => setOpenTimesheet(null)}>
              {t('common.general.close')}
            </MainButton>
          </div>
        </BaseSwipeableDrawer>
      )}
    </div>
  );
}
